import { useCallback, useEffect, useMemo } from 'react';
import {
  comparePlans,
  draftEntityChanges,
  pendingCodeChanges,
  type DraftEntityChanges,
} from '@repo/front-end-utils';
import type { EntityCodeChange, FloorPlannerLayout } from '@repo/frontend-types';

import type { LaunchPlans } from './useLaunchPlans';
import type { ChangesState } from './VersionsModal';
import { changeSummary } from './versionsText';

const NONE: EntityCodeChange[] = [];

/**
 * Where the draft-against-production comparison has got to, for the steps
 * (`onboarding-status-colours-change-source`, R2). `off` is "nothing wants it", or the store holds
 * another twin's plan; `never-launched` is a history read that names no production launch, never a
 * history still loading or failed.
 */
export type DraftComparisonState = 'off' | 'loading' | 'failed' | 'never-launched' | 'ready';

/** The page's launch-history read, as far as this source needs it: the last ready one is `ready`. */
export type DraftHistoryState = 'loading' | 'failed' | 'ready';

/**
 * The page's history read as the source takes it (R3, and the spec's Overtaken cell): the last READY
 * history for this twin wins, so a re-read loading or failing changes nothing; without one, a read
 * that failed FOR THIS TWIN is `failed`, and anything else — a failure still showing for the last
 * twin, before this twin's read starts — is `loading`, never "never launched".
 */
export const draftHistoryState = (
  lastReadyForThisTwin: unknown,
  read: { state: 'loading' | 'failed' | 'ready'; twinId?: number },
  twinId: number | undefined,
): DraftHistoryState =>
  lastReadyForThisTwin !== null && lastReadyForThisTwin !== undefined
    ? 'ready'
    : read.state === 'failed' && read.twinId === twinId
      ? 'failed'
      : 'loading';

/** What the page has open, as the draft comparison's wanting reads it. */
export interface DraftChangesWants {
  /** Whether Discard is offered: a draft that differs from production. */
  canDiscard: boolean;
  versionsOpen: boolean;
  discardOpen: boolean;
  loadOpen: boolean;
}

/**
 * Whether the dialogs show the draft's changes: Versions or Discard on a draft Discard is offered for,
 * or Load on any draft (`load-launch`, R3.1). The dialogs read the source's `changes` only while this
 * holds, so a step wanting the comparison never adds a changes block to a dialog
 * (`onboarding-status-colours-plan-spaces`, R8).
 */
export const dialogsWantChanges = ({
  canDiscard,
  versionsOpen,
  discardOpen,
  loadOpen,
}: DraftChangesWants): boolean => (canDiscard && (versionsOpen || discardOpen)) || loadOpen;

/**
 * Whether the page wants the draft compared with production: whenever the dialogs show its changes;
 * on Floor Plan, whose lasting marks read it entity by entity (`onboarding-status-colours-plan-spaces`,
 * R8); and on Observers, whose "Added in this draft" group reads it
 * (`onboarding-status-colours-observers`, R2).
 */
export const draftChangesWanted = (
  wants: DraftChangesWants & { onFloorPlan: boolean; onObservers: boolean },
): boolean => dialogsWantChanges(wants) || wants.onFloorPlan || wants.onObservers;

export interface UseDraftChangesParams {
  /**
   * Something on the page wants the comparison: Versions, Discard or Load showing the changes
   * (R4.4), or the Floor Plan or Observers step reading it entity by entity (`draftChangesWanted`); nothing is
   * read and `comparison` is `off` otherwise. Required, with no default.
   */
  wanted: boolean;
  /**
   * Which dialog is showing them. Each opening asks again, so a plan whose last read failed is
   * re-read when the next dialog opens, though `wanted` stayed true across the hand-over.
   */
  dialog: string | null;
  /** The page's launch-plan cache (`useLaunchPlans`), shared with the Load confirmation. */
  plans: LaunchPlans;
  /** The twin's launch history: `ready` with `productionUuid: null` is a twin never launched. */
  history: DraftHistoryState;
  /** Production's launch — the base the draft is compared with. */
  productionUuid: string | null;
  /** The plan the wizard holds now, unsaved edits included. The dialogs read this one. */
  layout: FloorPlannerLayout | null;
  /** `layout` when the store is scoped to this page session, else `null`. */
  sessionLayout: FloorPlannerLayout | null;
  /**
   * The session's plan was loaded under the twin now on screen (`sessionPlanTwinBid === twinBid`).
   * `?twinBid=` can change without a remount, and the session then KEEPS the last twin's plan, so the
   * session scope alone does not make it this twin's (R7). Required, with no default.
   */
  planIsThisTwins: boolean;
  twinBid: string | null;
}

export interface DraftChangesSource {
  /**
   * The changes block, whenever the comparison is wanted. Versions, Discard and Load read it only
   * while `dialogsWantChanges` holds, which keeps them exactly as before this source existed.
   */
  changes: ChangesState | null;
  retry: () => void;
  comparison: DraftComparisonState;
  /**
   * Entity by entity: added, moved, rezoned or renumbered, and its step. Renumbering alone unless
   * `ready`; empty unless the plan is this twin's (R7).
   */
  byEntity: DraftEntityChanges;
  /**
   * The pending renumbering of the session's plan, in every comparison state: *Renumbered at Launch*
   * reads it. Empty when the store holds another session's plan, and when the session's plan was
   * loaded under another twin (`onboarding-status-colours-launch`, R9), so a twin switched in place
   * never lists the last twin's spaces.
   */
  renumbered: EntityCodeChange[];
}

/**
 * The page's one "what changed in this draft" source (`onboarding-status-colours-change-source`; the
 * dialog half is `twin-details-page-versions`, R4): production's plan read through the page's cache —
 * a completed launch's plan never changes, so it is read once per page session — compared once with
 * the plan on screen by `comparePlans`, and projected both as the dialogs' counts and, entity by
 * entity, for the steps. Both come from the one comparison, so they can never disagree.
 *
 * A failure is said as one — never as "no changes" — with `retry` to read again.
 */
export const useDraftChanges = ({
  wanted,
  dialog,
  plans,
  history,
  productionUuid,
  layout,
  sessionLayout,
  planIsThisTwins,
  twinBid,
}: UseDraftChangesParams): DraftChangesSource => {
  const { request, retry: retryPlan } = plans;
  useEffect(() => {
    if (wanted && productionUuid) request(productionUuid);
    // `dialog` is a dependency only as the opening it marks.
  }, [wanted, dialog, productionUuid, request]);

  const retry = useCallback(() => {
    if (productionUuid) retryPlan(productionUuid);
  }, [productionUuid, retryPlan]);

  // Read on every plan change, wanted or not (R8), and only from this session's plan (R7).
  const renumbered = useMemo(
    () => (sessionLayout && twinBid ? pendingCodeChanges(sessionLayout, twinBid) : []),
    [sessionLayout, twinBid],
  );

  /*
   * Production's plan read and compared with `layout` once, over exactly the inputs the dialogs have
   * always depended on, so their `changes` is the same value as before (R5).
   */
  const read = useMemo(() => {
    if (!wanted || !productionUuid) return null;
    const plan = plans.read(productionUuid);
    if (!plan || plan.state === 'loading') {
      return { changes: { state: 'loading' } as ChangesState, comparison: null };
    }
    if (plan.state === 'failed') {
      return { changes: { state: 'failed' } as ChangesState, comparison: null };
    }
    const comparison = comparePlans(plan.plan, layout, { twinBid });
    const changes: ChangesState = {
      state: 'ready',
      pills: changeSummary(comparison),
      total: comparison.total,
    };
    return { changes, comparison };
  }, [wanted, productionUuid, plans, layout, twinBid]);

  // R7: the plan on screen is this session's AND was loaded under the twin on screen.
  const thisTwinsPlan = sessionLayout !== null && layout === sessionLayout && planIsThisTwins;
  const comparison = ((): DraftComparisonState => {
    // R4, then R7: nothing wants it, or the plan is not this twin's.
    if (!wanted || !thisTwinsPlan) return 'off';
    // R3: only a history actually read names a twin never launched.
    if (history !== 'ready') return history;
    if (!productionUuid) return 'never-launched';
    if (!read || read.changes.state === 'loading') return 'loading';
    return read.changes.state === 'failed' ? 'failed' : 'ready';
  })();
  // Gated on the state, not just the cached read: off, a history not yet ready and never launched
  // each win over a production plan already in the cache.
  const ready = comparison === 'ready' ? (read?.comparison ?? null) : null;
  // R2: the comparison's answers only when ready; the renumbering alone in every other state.
  const stepRenumbered = thisTwinsPlan ? renumbered : NONE;
  const byEntity = useMemo(
    () => draftEntityChanges(ready, stepRenumbered),
    [ready, stepRenumbered],
  );

  return {
    changes: read?.changes ?? null,
    retry,
    comparison,
    byEntity,
    renumbered: planIsThisTwins ? renumbered : NONE,
  };
};
